/* @flow */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import { translate } from 'react-i18next';
import Moment from 'react-moment';
import RequireForAccess from 'react-access';
import { Link } from 'react-router-dom';
import uuidv4 from 'uuid/v4';

import UserStore from 'stores/UserStore';
import { Roles } from 'permissions/defs';

import type TypeActionDrawerStore from 'stores/ActionDrawerStore.js';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Collapse from '@material-ui/core/Collapse';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Button from '@material-ui/core/Button';

//----------------------------------------------------------------------//
// OTHER                                                                //
//----------------------------------------------------------------------//

import Snackbar from '@material-ui/core/Snackbar';

import { updateDownloadAssetDocs } from 'permissions/asset';

//----------------------------------------------------------------------//
// CSS                                                                  //
//----------------------------------------------------------------------//

import styles from './DocumentList.module.scss';
import { ElectroTooltip } from '../ExtendedUiElements/customized';
import LoadingWrapper from '../LoadingWrapper/LoadingWrapper';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

export type DocumentListType = $Values<typeof DocumentListTypes>;
export const DocumentListTypes = Object.freeze({
  CALIBRATION_CERTIFICATE: 'calibration-certificate',
});

type InjectedProps = {
  actionDrawerStore: TypeActionDrawerStore,
  userStore: UserStore,
};

type DocumentListProps = {
  type: DocumentListType,
};

export type TypeDocumentListItem = {
  id?: string | number,
  icon?: string,
  name?: string,
  created_at?: string,
  type?: string,
  link?: string,
  linkToButton?: string,
  secondary?: string,
  linkTo?: string,
  folderId?: string,
  create?: boolean,
  showType?: boolean,
  onDelete?: Function,
};

export type TypeDocumentList = {
  collapsible?: boolean,
  header?: string,
  headerIcon?: string,
  headerUppercase?: boolean,
  listItems: *,
};

export type TypeExpandedList = {
  children: React.Node,
};

const documentIcon: Object = {
  pdf: '#icon-pdf',
};

const rolesAllowedToDelete = [Roles.SYSTEM_ADMIN];

function documentTypeIcon(docType) {
  const documentType: string = docType.toLowerCase();

  if (documentIcon[documentType]) {
    return (
      <svg>
        <title>{documentType.toUpperCase()}</title>
        <use xlinkHref={documentIcon[documentType]} />
      </svg>
    );
  }

  return documentType.toUpperCase();
}

@translate()
@inject('actionDrawerStore', 'userStore')
@observer
export class DocumentList extends ComponentWithInjectedProps<DocumentListProps, {}, InjectedProps> {
  static defaultProps = {
    collapsible: false,
    listItems: [
      {
        name: 'Document List Item',
      },
    ],
  };

  async downloadPDF(listItem: TypeDocumentListItem, loadingKey: string) {
    const { actionDrawerStore } = this.props;

    actionDrawerStore.setLoadingEntry(loadingKey, true);
    // there are two types of downloadable certificates
    // the one that we uploaded and the already existing in server from middleware
    listItem.create
      ? await actionDrawerStore.exportPdfFile(formatLink(listItem.link), listItem.name)
      : await actionDrawerStore.getUrlAsGlobalDownload(
          formatLink(listItem.link),
          listItem.name,
          listItem.type
        );

    actionDrawerStore.setLoadingEntry(loadingKey, false);
  }

  async openPDF(listItem: TypeDocumentListItem, loadingKey: string) {
    const { actionDrawerStore } = this.props;

    actionDrawerStore.setLoadingEntry(loadingKey, true);

    await actionDrawerStore.openPdf(formatLink(listItem.link), listItem.name);

    actionDrawerStore.setLoadingEntry(loadingKey, false);
  }

  async deleteDocument(listItem: TypeDocumentListItem, loadingKey: string) {
    const { actionDrawerStore } = this.props;

    actionDrawerStore.setLoadingEntry(loadingKey, true);

    await listItem.onDelete(listItem.folderId, listItem.id);

    actionDrawerStore.setLoadingEntry(loadingKey, false);
  }

  canUserDelete() {
    const { userStore: userRole } = this.props;
    return rolesAllowedToDelete.includes(userRole.activeUser.role_id);
  }

  renderListItem(listItem: TypeDocumentListItem, index: number): React.Element<*> {
    let showDeleteButton = listItem.onDelete !== undefined;
    const t: Function = this.props.t;
    const { type }: DocumentListType = this.props;
    const downloadDocumentLabel =
      type === DocumentListTypes.CALIBRATION_CERTIFICATE
        ? t('general:actions.download_document_calibration_certificate')
        : t('general:actions.download_document');

    const viewDocumentLabel =
      type === DocumentListTypes.CALIBRATION_CERTIFICATE
        ? t('general:actions.view_document_in_browser_calibration_certificate')
        : t('general:actions.view_document_in_browser');

    if (type === DocumentListTypes.CALIBRATION_CERTIFICATE) {
      showDeleteButton = this.canUserDelete() && listItem.onDelete !== undefined;
    }

    return (
      <ListItem key={index} className={styles.documentListItem} button={false}>
        {listItem.icon && (
          <ListItemIcon>
            <svg>
              <use xlinkHref={listItem.icon} />
            </svg>
          </ListItemIcon>
        )}
        {listItem.name &&
          (listItem.linkTo ? (
            <Link to={listItem.linkTo}>
              <ListItemText className={styles.name} primary={listItem.name} />
            </Link>
          ) : listItem.linkToButton ? (
            <React.Fragment>
              <ListItemText
                className={styles.name}
                primary={listItem.name}
                secondary={listItem.secondary ? listItem.secondary : null}
              />
              <ListItemSecondaryAction>
                <Link to={listItem.linkToButton}>
                  <Button color="primary" variant="contained">
                    {t('general:actions.view')}
                  </Button>
                </Link>
              </ListItemSecondaryAction>
            </React.Fragment>
          ) : (
            <ListItemText className={styles.name} primary={listItem.name} />
          ))}
        {listItem.created_at && (
          <ListItemText
            className={styles.created_at}
            primary={
              <React.Fragment>
                {t('general:added')}:&nbsp;
                <Moment date={listItem.created_at} format="MMMM DD, YYYY" />
              </React.Fragment>
            }
          />
        )}
        {listItem.showType && listItem.type && (
          <ListItemText className={styles.type} primary={documentTypeIcon(listItem.type)} />
        )}
        {listItem.link && (
          <RequireForAccess permissions={updateDownloadAssetDocs}>
            <LoadingWrapper type={'document-list-download'}>
              {loadingKey => (
                <a className={styles.link} onClick={() => this.downloadPDF(listItem, loadingKey)}>
                  <ElectroTooltip title={downloadDocumentLabel}>
                    <ListItemIcon>
                      <svg>
                        <use xlinkHref="#icon-download" />
                      </svg>
                    </ListItemIcon>
                  </ElectroTooltip>
                </a>
              )}
            </LoadingWrapper>
            {listItem.type.toLowerCase() === 'pdf' && (
              <LoadingWrapper type={'document-list-view'}>
                {loadingKey => (
                  <a
                    className={`${styles.link} ${styles.icon}`}
                    onClick={() => this.openPDF(listItem, loadingKey)}
                  >
                    <ElectroTooltip title={viewDocumentLabel}>
                      <ListItemIcon>
                        <svg className={styles.preview}>
                          <use xlinkHref="#icon-view" />
                        </svg>
                      </ListItemIcon>
                    </ElectroTooltip>
                  </a>
                )}
              </LoadingWrapper>
            )}
          </RequireForAccess>
        )}
        {showDeleteButton && (
          <LoadingWrapper type={'document-list-delete'}>
            {loadingKey => (
              <a
                className={styles['remove-link']}
                onClick={() => this.deleteDocument(listItem, loadingKey)}
              >
                <ElectroTooltip title={t('general:actions.delete')}>
                  <ListItemIcon>
                    <svg>
                      <use xlinkHref="#icon-declined" />
                    </svg>
                  </ListItemIcon>
                </ElectroTooltip>
              </a>
            )}
          </LoadingWrapper>
        )}
      </ListItem>
    );
  }

  renderList() {
    const { t } = this.props;
    const { listItems }: TypeDocumentList = this.props;
    const isObject = listItems.constructor.name === 'Object';
    let list: Array<TypeDocumentListItem> = listItems.length ? listItems : [];

    if (isObject) {
      list = [listItems];
    }

    return list.length > 0 ? (
      <List component="ul" className={styles.documentList} disablePadding={true}>
        {list.map(this.renderListItem.bind(this))}
      </List>
    ) : (
      <div>{t('general:accounts.messages.no_documents')}</div>
    );
  }

  render() {
    return this.props.collapsible ? (
      <ExpandedList {...this.props}>{this.renderList()}</ExpandedList>
    ) : (
      this.renderList()
    );
  }
}

@translate()
@inject('actionDrawerStore')
@observer
export class ExpandedList extends React.Component<*> {
  @observable isExpanded: boolean = false;

  componentDidMount() {
    // for contract's products the list should be exapnded
    // if the user clicks on products qty column in the table
    if (this.props.isExpanded) {
      this.isExpanded = this.props.isExpanded;
    }
  }

  static defaultProps = {
    header: 'Expanded List Header',
    headerUppercase: true,
  };

  @computed get status() {
    return this.props.actionDrawerStore.documentError;
  }

  renderCollapse(): React.Element<*> {
    const classNames = this.isExpanded ? styles.isExpanded : styles.expandedCollapse;

    return (
      <Collapse in={this.isExpanded} timeout="auto" unmountOnExit={true} className={classNames}>
        {this.props.children}
      </Collapse>
    );
  }

  renderListItemHeader(): React.Element<*> {
    const headerStyle: string = this.props.headerUppercase ? styles.uppercase : '';

    return (
      <ListItem
        button={false}
        className={`${styles.expandedListItemHeader} ${headerStyle}`}
        onClick={() => {
          this.isExpanded = !this.isExpanded;
        }}
      >
        {this.props.headerIcon && (
          <ListItemIcon>
            <svg>
              <use xlinkHref={this.props.headerIcon} />
            </svg>
          </ListItemIcon>
        )}
        <ListItemText primary={this.props.header} />
        <ListItemIcon className={styles.collapseIcon}>
          <svg>
            <use xlinkHref={this.isExpanded ? '#icon-chevron-down' : '#icon-chevron-up'} />
          </svg>
        </ListItemIcon>
      </ListItem>
    );
  }

  render(): React.Element<*> {
    const { t } = this.props;

    return (
      <List component="ul" className={styles.expandedList} disablePadding={true}>
        {this.renderListItemHeader()}
        {this.renderCollapse()}
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={this.status}
          onClose={() => {
            this.props.actionDrawerStore.documentError = false;
          }}
          autoHideDuration={3000}
          ContentProps={{
            'aria-describedby': 'message-id',
          }}
          message={<span id="message-id">{t('general:messages.error_occured')}</span>}
        />
      </List>
    );
  }
}

const formatLink = (link: ?string) => {
  if (!link) {
    return '';
  }

  if (link[0] === '/') {
    return link.substring(1);
  }

  return link;
};
